import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";

export interface PermissionRole {
  id: string;
  role_key: string | null;
  name: string;
  level: number;
}

export interface PermissionMeta {
  key: string;
  label: string;
  category: string;
  /** True when this permission is intentionally excluded from role:platform:manager */
  platformManagerLocked: boolean;
}

export interface RolePermissionMatrix {
  roles: PermissionRole[];
  /** role_id → permission_key → allowed */
  matrix: Record<string, Record<string, boolean>>;
  permissions: PermissionMeta[];
  permission_keys: string[]; // kept for backwards compat
}

export function useRolePermissions(enabled = true) {
  return useQuery<RolePermissionMatrix>({
    queryKey: ["admin", "permissions"],
    queryFn: async () => {
      const res = await api.get<{ data: RolePermissionMatrix }>(
        "/admin/permissions",
      );
      return res.data.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useUpdateRolePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      role_id: string;
      permission_key: string;
      allowed: boolean;
    }) => api.put("/admin/permissions", payload),

    // Optimistic update so the checkbox feels instant
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "permissions"] });
      const previous = queryClient.getQueryData<RolePermissionMatrix>([
        "admin",
        "permissions",
      ]);

      if (previous) {
        queryClient.setQueryData<RolePermissionMatrix>(
          ["admin", "permissions"],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              matrix: {
                ...old.matrix,
                [payload.role_id]: {
                  ...old.matrix[payload.role_id],
                  [payload.permission_key]: payload.allowed,
                },
              },
            };
          },
        );
      }

      return { previous };
    },

    // US-081: onError keeps only the optimistic rollback. The toast comes
    // from the global MutationCache handler, which already prefers the
    // backend's reason (response.data.error, e.g. a 400 rejecting a
    // legacy-key grant) over errorFallback — a toast here would double up.
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["admin", "permissions"], ctx.previous);
      }
    },
    meta: { errorFallback: "Failed to update permission" },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "permissions"] });
    },
  });
}
